
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { toast } from 'sonner';

interface RenameSessionDialogProps {
  sessionId: string;
  currentName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RenameSessionDialog: React.FC<RenameSessionDialogProps> = ({
  sessionId,
  currentName,
  isOpen,
  onOpenChange,
}) => {
  const { updateSessionTitle } = useAiAssistant();
  const [name, setName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Session name cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await updateSessionTitle(sessionId, name.trim());
      if (success) {
        toast.success('Session renamed successfully');
        onOpenChange(false);
      } else {
        toast.error('Failed to rename session');
      }
    } catch (error) {
      console.error('Error renaming session:', error);
      toast.error('Failed to rename session');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background/95 backdrop-blur-xl border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename Session</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Enter a new name for this session.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter session name"
              className="bg-background/30 border-white/10"
              autoFocus
            />
          </div>
          <DialogFooter className="pt-4">
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => onOpenChange(false)}
              className="border-white/10"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting || !name.trim() || name === currentName}
              className="bg-primary hover:bg-primary/80"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
